import React, { useState, useEffect } from 'react';
import { 
  Play, 
  CheckCircle, 
  Clock, 
  Calendar, 
  Eye, 
  Plus,
  Sparkles,
  FileText,
  Mail,
  Video,
  Twitter,
  Instagram,
  Moon,
  Sun
} from 'lucide-react';
import '../../styles/studio-design-system.css';

interface Project {
  id: string;
  title: string;
  guest: string;
  status: 'processing' | 'ready' | 'scheduled' | 'published';
  createdAt: Date;
  processedAt?: Date;
  publishedAt?: Date;
  assets?: {
    blogPost: { wordCount: number; ready: boolean };
    newsletter: { wordCount: number; ready: boolean };
    socialClips: { count: number; ready: boolean };
    tweets: { count: number; ready: boolean };
  };
  stats?: {
    platforms: number;
    views: number;
    engagement: number;
  };
}

interface ProactiveDashboardProps {
  onProjectSelect?: (projectId: string) => void;
}

const ProactiveDashboard: React.FC<ProactiveDashboardProps> = ({ onProjectSelect }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Simulate loading projects
    setTimeout(() => {
      setProjects([
        {
          id: '1',
          title: 'Interview with Dr. Sarah Chen',
          guest: 'Dr. Sarah Chen',
          status: 'ready',
          createdAt: new Date('2024-01-15T10:00:00'),
          processedAt: new Date('2024-01-15T10:45:00'),
          assets: {
            blogPost: { wordCount: 2150, ready: true },
            newsletter: { wordCount: 450, ready: true },
            socialClips: { count: 5, ready: true },
            tweets: { count: 10, ready: true }
          }
        },
        {
          id: '2',
          title: 'Tech Industry Analysis',
          guest: 'Mike Rodriguez',
          status: 'processing',
          createdAt: new Date('2024-01-15T14:30:00'),
          assets: {
            blogPost: { wordCount: 0, ready: false },
            newsletter: { wordCount: 0, ready: false },
            socialClips: { count: 0, ready: false },
            tweets: { count: 0, ready: false }
          }
        },
        {
          id: '3',
          title: 'Startup Funding Trends',
          guest: 'Alex Thompson',
          status: 'published',
          createdAt: new Date('2024-01-14T09:00:00'),
          publishedAt: new Date('2024-01-14T15:30:00'),
          stats: {
            platforms: 4,
            views: 12500,
            engagement: 8.5
          }
        }
      ]);
      setIsLoading(false);
    }, 1000);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processing':
        return <Clock className="w-4 h-4" />;
      case 'ready':
        return <CheckCircle className="w-4 h-4" />;
      case 'scheduled':
        return <Calendar className="w-4 h-4" />;
      case 'published':
        return <Eye className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'processing':
        return 'Processing...';
      case 'ready':
        return 'Ready for Review';
      case 'scheduled':
        return 'Scheduled';
      case 'published':
        return 'Published';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processing':
        return 'text-gray-500';
      case 'ready':
        return 'text-green-600';
      case 'scheduled':
        return 'text-blue-600';
      case 'published':
        return 'text-gray-500';
      default:
        return 'text-gray-500';
    }
  };

  const renderProjectCard = (project: Project) => {
    const cardClass = `card ${
      project.status === 'ready' ? 'card-ready' : 
      project.status === 'processing' ? 'card-processing' : 
      'card-completed'
    }`;

    return (
      <div key={project.id} className={cardClass}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-card-title mb-1">{project.title}</h3>
            <div className={`status-${project.status} text-sm`}>
              {getStatusIcon(project.status)}
              <span className="ml-2">{getStatusText(project.status)}</span>
            </div>
          </div>
          {project.status === 'ready' && (
            <div className="magic-tag">
              <Sparkles className="w-3 h-3" />
              AI Assets Ready
            </div>
          )}
        </div>

        {project.status === 'processing' && (
          <div className="text-subtle text-sm">
            Estimated 4 minutes remaining
          </div>
        )}

        {project.status === 'ready' && project.assets && (
          <div className="mb-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>Blog Post ({project.assets.blogPost.wordCount} words)</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>Newsletter Draft ({project.assets.newsletter.wordCount} words)</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>{project.assets.socialClips.count} Social Clips (Vertical)</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>{project.assets.tweets.count} Tweet Ideas</span>
              </div>
            </div>
            <button 
              className="btn-primary mt-4 w-full"
              onClick={() => onProjectSelect?.(project.id)}
            >
              Review & Publish →
            </button>
          </div>
        )}

        {project.status === 'published' && project.stats && (
          <div className="text-subtle text-sm">
            Published to {project.stats.platforms} platforms • {project.stats.views.toLocaleString()} views
            <button className="text-blue-600 hover:underline ml-2">
              View Analytics
            </button>
          </div>
        )}

        {project.status === 'scheduled' && (
          <div className="text-subtle text-sm">
            Scheduled for Oct 20, 2024
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark-mode' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} border-b`}>
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-page-title">Studio Dashboard</h1>
              <p className="text-subtle mt-1">Your AI-powered content production hub</p>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="btn-secondary flex items-center gap-2"
              >
                {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button className="btn-primary flex items-center gap-2">
                <Plus className="w-4 h-4" />
                New Recording
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-6">
        <div className="space-y-4">
          {projects.map(renderProjectCard)}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Play className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No recordings yet</h3>
            <p className="text-gray-600 mb-4">Start by recording your first interview or conversation</p>
            <button className="btn-primary">
              Start Recording
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProactiveDashboard;
